// GHL-PROVIDER (build order increment 2 B and §3.1a; integrations design §2.8, §3 #11): the
// connector-mode CRM. A DE's `crm_*` tools name no vendor: the company's CRM mode
// (`company.settings.crm_mode`, default `connector`) decides the backend, and in connector mode
// the backend is the company's GoHighLevel connection. Behind the company flag `ghl_connector`
// (default off): every route answers 404 while it is off.
//
//   POST {co}/ghl/connections   owners and admins: connect a GHL location (a Private Integration
//                               token, checked against the location before it is sealed) or
//                               activate a marketplace OAuth connection for one location; either
//                               way the location's notices route into the intake
//   POST {co}/crm/read          members (people and DEs): one CRM read through the connector
//                               (contacts, opportunities, pipelines, a timeline, conversations);
//                               a read changes nothing here (`safe`)
//   POST {co}/crm/acts          members: one outward CRM act (`crm.write`, `message.send`). The
//                               connector policy decides: notes, tasks, tags and the grant's
//                               direct fields go to the outbox now (`queued`); anything else — a
//                               stage move, a locked field, every message — answers
//                               `approval_required` with the exact change, which the caller
//                               files as an `external_action` approval. `message.send` is never
//                               direct.
import { z } from "zod";
import { ToolConnection } from "../connection-rows";
import { ExternalActionChange } from "../outbox-rows";
import { LIMITS, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const core = { feature: "core", tag: "crm" } as const;

/** A GoHighLevel location id (alphanumeric, as the vendor issues them). */
const LocationId = z
  .string()
  .trim()
  .min(4)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, "a GoHighLevel location id");

export const GhlConnect = z
  .discriminatedUnion("auth", [
    z.strictObject({
      /** A Private Integration token made in the location's settings (sealed, never returned). */
      auth: z.literal("private_integration"),
      token: z.string().trim().min(16).max(512),
      location_id: LocationId,
      label: z.string().trim().min(1).max(LIMITS.name).optional(),
    }),
    z.strictObject({
      /** A company-scope `ghl` connection made through the marketplace app's OAuth. */
      auth: z.literal("oauth"),
      connection_id: Uuid,
      /** Required for an agency (Company) token: the location it works; a Location token's own. */
      location_id: LocationId.optional(),
    }),
  ])
  .meta({ id: "GhlConnect" });
export type GhlConnect = z.infer<typeof GhlConnect>;

export const GhlConnected = z
  .object({
    connection: ToolConnection,
    location_id: z.string(),
    /** The intake subscription the location's notices (and the polling fallback) arrive by. */
    subscription_id: Uuid,
    /** The app webhook events wired into the intake. */
    webhook_events: z.array(z.string()),
  })
  .meta({ id: "GhlConnected" });

export const CRM_READ_TOOLS = [
  "crm_search",
  "crm_get",
  "crm_list",
  "crm_pipelines",
  "crm_timeline",
  "messages_list",
] as const;
export const CrmObject = z.enum(["contact", "opportunity"]);

export const CrmRead = z
  .strictObject({
    tool: z.enum(CRM_READ_TOOLS),
    /** The connection (absent: the company's one live CRM connection). */
    connection_id: Uuid.optional(),
    object: CrmObject.optional(),
    /** `crm_get`, `crm_timeline`, `messages_list`: the record (or conversation) id. */
    id: z.string().trim().min(1).max(LIMITS.name).optional(),
    /** `crm_search`: free text. */
    q: z.string().trim().max(LIMITS.name).optional(),
    /** `crm_list`: the vendor-neutral filter. */
    filter: z
      .strictObject({
        pipeline_id: z.string().max(LIMITS.name).optional(),
        stage_id: z.string().max(LIMITS.name).optional(),
        status: z.enum(["open", "won", "lost", "abandoned"]).optional(),
        contact_id: z.string().max(LIMITS.name).optional(),
        tag: z.string().max(LIMITS.name).optional(),
      })
      .optional(),
    limit: z.int().min(1).max(100).optional(),
    /** The previous answer's `next_cursor`. */
    cursor: z.string().max(LIMITS.text).optional(),
  })
  .meta({ id: "CrmRead" });
export type CrmRead = z.infer<typeof CrmRead>;

export const CrmReadResult = z
  .object({
    connection_id: Uuid,
    provider: z.string(),
    mode: z.literal("connector"),
    /** The projection (records cut to what an agent needs; text in them is people's data). */
    data: z.unknown(),
    next_cursor: z.string().nullable(),
  })
  .meta({ id: "CrmReadResult" });
export type CrmReadResult = z.infer<typeof CrmReadResult>;

export const CrmAct = z
  .strictObject({
    /** `crm.write` or `message.send`; `connection_id` absent: the company's CRM connection. */
    change: ExternalActionChange,
    /** One line for the approver. */
    summary: z.string().trim().min(1).max(LIMITS.text).optional(),
  })
  .meta({ id: "CrmAct" });
export type CrmAct = z.infer<typeof CrmAct>;

export const CrmActResult = z
  .object({
    /** `queued`: in the outbox now; `approval_required`: file `change` as an approval. */
    outcome: z.enum(["queued", "approval_required"]),
    outbox_id: Uuid.nullable(),
    /** The exact act (its connection resolved): what goes out, or what the approval carries. */
    change: ExternalActionChange,
    title: z.string(),
    reason: z.string(),
  })
  .meta({ id: "CrmActResult" });
export type CrmActResult = z.infer<typeof CrmActResult>;

export const crmRoutes = {
  "ghlConnections.create": defineRoute({
    ...core,
    id: "ghlConnections.create",
    method: "post",
    path: `${co}/ghl/connections`,
    summary: "Connect a GoHighLevel location (Private Integration token, or an OAuth connection)",
    access: "manager",
    params: CompanyParams,
    body: GhlConnect,
    idempotency: "optional",
    response: { status: 201, schema: GhlConnected },
    errors: ["validation_failed", "conflict", "connection_unusable", "vendor_unavailable"],
    emits: ["tool_connection.added"],
  }),
  "crm.read": defineRoute({
    ...core,
    id: "crm.read",
    method: "post",
    path: `${co}/crm/read`,
    summary: "One CRM read through the company's CRM connection (connector mode)",
    access: "member",
    params: CompanyParams,
    body: CrmRead,
    safe: true,
    response: { status: 200, schema: CrmReadResult },
    errors: [
      "validation_failed",
      "conflict",
      "connection_grant_missing",
      "connection_unusable",
      "vendor_unavailable",
    ],
    emits: [],
  }),
  "crm.act": defineRoute({
    ...core,
    id: "crm.act",
    method: "post",
    path: `${co}/crm/acts`,
    summary: "One outward CRM act: queued when the connector policy allows it, else to approve",
    access: "member",
    params: CompanyParams,
    body: CrmAct,
    idempotency: "optional",
    response: { status: 200, schema: CrmActResult },
    errors: [
      "validation_failed",
      "conflict",
      "policy_denied",
      "connection_grant_missing",
      "connection_unusable",
    ],
    emits: ["outbox.queued"],
  }),
} as const;
